import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  Injector,
  OnInit,
  computed,
  inject,
  input,
  runInInjectionContext,
  signal,
} from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'

import { FlxStyleModule, FlxThemeModule, FlxWrapperStyleDirective } from '@flowx/angular-theme'
import {
  CollectionPrototypeTemplateConfig,
  ProcessActionTriggerEvent,
  ResolvedAction,
  getActionsByTemplateConfigAndTrigger$,
} from '@flowx/core-sdk'

/* eslint-disable import/no-cycle */
import { FlxTreeNodeComponent } from '../../renderer/tree-node/tree-node.component'
import { FlxActionService } from '../../services/flx-action.service'
import { getBoolExpressionSignal } from '../helpers'

@Component({
  selector: 'flx-collection-prototype',
  hostDirectives: [
    { directive: FlxWrapperStyleDirective, inputs: ['flxWrapperStyle: templateConfig'] },
  ],
  imports: [FlxTreeNodeComponent, FlxThemeModule, FlxStyleModule],
  templateUrl: './flx-collection-prototype.component.html',
  styleUrl: './flx-collection-prototype.component.scss',
  host: {
    '[class]': `displayOptions().className`,
    '[attr.role]': `hasClickAction() ? 'button' : undefined`,
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FlxCollectionPrototypeComponent implements OnInit {
  #actionService = inject(FlxActionService)
  #injector = inject(Injector)
  #destroyRef = inject(DestroyRef)

  // actions for the CLICK trigger – kept in sync with the process-actions store
  // so they arrive even when the node's details resolve after this component mounts.
  #clickActions = signal<ResolvedAction[]>([])

  templateConfig = input.required<CollectionPrototypeTemplateConfig>()

  displayOptions = computed(() => this.templateConfig().displayOptions)
  flowxProps = computed(() => this.displayOptions().flowxProps)
  style = computed(() => this.displayOptions().style)

  evaluatedConditionalStyles = signal([])
  conditionalStyles = computed(() =>
    this.evaluatedConditionalStyles()
      .filter(({ expression }) => expression())
      .map(({ overrides }) => overrides)
  )

  hasClickAction = computed(() => this.#clickActions().length > 0)

  ngOnInit(): void {
    getActionsByTemplateConfigAndTrigger$(this.templateConfig(), ProcessActionTriggerEvent.CLICK)
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe((actions) => this.#clickActions.set(actions))

    runInInjectionContext(this.#injector, () => {
      const { conditionals } = this.style()
      if (Array.isArray(conditionals)) {
        this.evaluatedConditionalStyles.set(
          conditionals.map(({ condition, overrides }, index) => ({
            expression: getBoolExpressionSignal(condition, this.templateConfig(), {
              logType: 'conditional',
              conditionIndex: index,
            }),
            overrides,
          }))
        )
      }
    })
  }

  async onClick(): Promise<void> {
    if (!this.hasClickAction()) {
      return
    }
    await this.#actionService.runActions(this.#clickActions(), this.templateConfig())
  }
}
